import { Injectable } from "@angular/core";
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { Router } from "@angular/router";
import { hasPermissionByKey, isAdmin, isSupervisor } from "@app/_helpers/permission";
import {
	EvaluateWorkingHoursOptions,
	evaluateWorkingHours,
	type WorkingHourViolation,
} from "@app/_helpers/rulebreakUtil";
import { getActiveSchedule } from "@app/_helpers/utils";
import {
	ComeAndGoRulebreakFixDialogComponent,
	WorkingHoursUpdateData,
} from "@app/components/come-and-go-rulebreak-dialog/come-and-go-rulebreak-dialog.component";
import { ComegoSuggestionService } from "@app/services/come-and-go-suggestion.service";
import {
	ComegoQuery,
	ComegoService,
	ComegoStore,
	ComegoTime,
	Logger,
	UserSettings,
	UserSettingsQuery,
} from "@timeghost/web-api";
import {
	addDays,
	addHours,
	addMilliseconds,
	endOfDay,
	isBefore,
	isSameDay,
	isSameMinute,
	min as minDate,
	roundToNearestMinutes,
	set,
	startOfDay,
} from "date-fns/esm";
import { firstValueFrom } from "rxjs";
import { uuidv7 } from "uuidv7";

const log = new Logger("ComegoRecordingRulebreak");
const BREAK_EXCEED_SESSION_KEY_PREFIX = "tg-comego-break-exceed-shown:";
/** End of day cut when stopping a recording that would otherwise span midnight. */
const DAY_CUT_HOUR = 23;
const DAY_CUT_MINUTE = 59;
/** Max auto-created pause length when cut point and now span long gaps (e.g. multi-day recordings). */
const MAX_AUTO_PAUSE_MS = 10 * 60 * 60 * 1000;
/** When the open recording started at least this long ago, use rule break minutes instead of the 10h pause cap. */
const LONG_RECORDING_THRESHOLD_MS = 12 * 60 * 60 * 1000;
/** Fallback daily schedule target when the user has no enabled schedule for the day. */
const DEFAULT_SCHEDULE_TARGET_MS = 8 * 60 * 60 * 1000;

/** Generates a client-only id for draft entries that have never been persisted. */
const makeDraftId = () => `draft-${uuidv7()}`;

const isDraftId = (id?: string | null): boolean => id?.startsWith("draft-") ?? false;

/** Dialog close value: user chose to stop without applying the suggested correction. */
export const PROCEED_WITH_NORMAL_STOP = Symbol("PROCEED_WITH_NORMAL_STOP");

export type BeforeStopResult =
	| { action: "proceed" }
	| { action: "cancelled" }
	| { action: "fixed"; entries: ComegoTime[] };

type OpenRecordingInput = ComegoTime & {
	recording?: boolean;
	userId?: string;
	type?: ComegoTime["type"] | { name?: string };
};

interface StopRuleViolationPayload {
	openRecording: ComegoTime;
	dialogData: WorkingHoursUpdateData;
	laterDayDialogData?: WorkingHoursUpdateData;
	breakSessionKey: string;
	preventDeviatingWorkingHours: boolean;
}

/**
 * Evaluates break-rule violations before stopping a work recording and opens the rulebreak fix dialog when needed.
 */
@Injectable({ providedIn: "root" })
export class ComegoRecordingRulebreakService {
	constructor(
		private comegoService: ComegoService,
		private comegoQuery: ComegoQuery,
		private userSettingsQuery: UserSettingsQuery,
		private comegoSuggestion: ComegoSuggestionService,
		private router: Router,
		private dialog: MatDialog
	) {}

	/**
	 * Runs the break-rule check before stopping a recording. Returns whether the caller should proceed with
	 * the normal stop, cancel, or use entries created by the fix dialog.
	 */
	async resolveBeforeStop(
		rawEntry: ComegoTime & {
			recording?: boolean;
			userId?: string;
			type?: ComegoTime["type"] | { name?: string };
		},
		options?: { skipNavigate?: boolean }
	): Promise<BeforeStopResult> {
		const payload = await this.evaluateStopRuleViolation(rawEntry, {
			skipSessionGuard: true,
		});
		if (!payload) return { action: "proceed" };

		const result = await this.runRecordingFixDialogSequence(payload, {
			...options,
			skipNavigate: true,
			skipSessionGuard: true,
		});
		if (result === PROCEED_WITH_NORMAL_STOP) return { action: "proceed" };
		if (!result) return { action: "cancelled" };
		return { action: "fixed", entries: result };
	}

	/** Returns true when stopping the recording would trigger the rulebreak fix flow. */
	async hasStopRuleViolation(
		rawEntry: OpenRecordingInput,
		options?: { skipSessionGuard?: boolean }
	): Promise<boolean> {
		return !!(await this.evaluateStopRuleViolation(rawEntry, options));
	}

	/**
	 * Break-rule enforcement for a specific open work recording (self or subordinate).
	 * Returns the dialog ref when the rulebreak fix dialog was opened, otherwise null.
	 *
	 * Same-day recordings: corrected work stop only. Cross-day recordings: stop, pause,
	 * and optional schedule fill/resume drafts. All are built in-memory as draft entries
	 * and passed to the dialog. No API writes happen before the user submits.
	 */
	async maybePromptBreakRuleForOpenRecording(
		rawEntry: OpenRecordingInput,
		options?: { skipNavigate?: boolean; skipSessionGuard?: boolean }
	): Promise<MatDialogRef<ComeAndGoRulebreakFixDialogComponent> | null> {
		if (!options?.skipSessionGuard && this.dialog.openDialogs?.length) return null;

		const payload = await this.evaluateStopRuleViolation(rawEntry, options);
		if (!payload) return null;

		if (!options?.skipSessionGuard && typeof sessionStorage !== "undefined") {
			sessionStorage.setItem(payload.breakSessionKey, "1");
		}

		if (!options?.skipNavigate) {
			const path = (this.router.url ?? "").split("#")[0].split("?")[0];
			const onTimer = path === "/timer" || path.endsWith("/timer");
			if (!onTimer) {
				try {
					await this.router.navigateByUrl("/timer");
				} catch (e) {
					log.error(e);
				}
			}
		}

		await this.runRecordingFixDialogSequence(payload, options);
		return null;
	}

	private async runRecordingFixDialogSequence(
		payload: StopRuleViolationPayload,
		options?: { skipNavigate?: boolean; skipSessionGuard?: boolean }
	): Promise<ComegoTime[] | null | typeof PROCEED_WITH_NORMAL_STOP> {
		const firstResult = await this.openBreakRuleDialogAndAwait(payload.dialogData);
		if (firstResult === PROCEED_WITH_NORMAL_STOP) return PROCEED_WITH_NORMAL_STOP;
		if (!firstResult) return null;

		let allEntries = Array.isArray(firstResult) ? [...firstResult] : [firstResult];

		if (payload.laterDayDialogData) {
			const secondResult = await this.openBreakRuleDialogAndAwait(payload.laterDayDialogData);
			if (secondResult === PROCEED_WITH_NORMAL_STOP) return PROCEED_WITH_NORMAL_STOP;
			if (!secondResult) return null;
			const secondEntries = Array.isArray(secondResult) ? secondResult : [secondResult];
			allEntries = [...allEntries, ...secondEntries];
		}

		return allEntries;
	}

	private openBreakRuleDialogAndAwait(
		data: WorkingHoursUpdateData
	): Promise<ComegoTime[] | ComegoTime | null | typeof PROCEED_WITH_NORMAL_STOP> {
		return firstValueFrom(
			this.dialog
				.open(ComeAndGoRulebreakFixDialogComponent, {
					data,
					disableClose: true,
				})
				.afterClosed()
		);
	}

	private async evaluateStopRuleViolation(
		rawEntry: OpenRecordingInput,
		options?: { skipSessionGuard?: boolean }
	): Promise<StopRuleViolationPayload | null> {
		const openRecording = this.normalizeOpenRecording(rawEntry);
		if (!openRecording) return null;

		const user = this.userSettingsQuery.getValue();
		if (!user?.id || !user.workspace?.id) return null;
		if (!user.workspace.settings?.workinghoursEnabled) return null;

		const wh = user.workspace.schedules?.workspace?.workinghours;
		if (!wh?.breaks?.length) return null;

		const targetUserId = openRecording.user?.id;
		if (!targetUserId) return null;
		if ((openRecording as ComegoTime & { deleted?: boolean }).deleted) return null;

		const isSubordinate = targetUserId !== user.id;
		if (
			isSubordinate &&
			!isAdmin(user) &&
			!isSupervisor(user) &&
			!hasPermissionByKey(user, "groupsCanComegoViewTimes")
		) {
			return null;
		}

		const breakSessionKey = `${BREAK_EXCEED_SESSION_KEY_PREFIX}${openRecording.id}`;
		if (
			!options?.skipSessionGuard &&
			typeof sessionStorage !== "undefined" &&
			sessionStorage.getItem(breakSessionKey)
		) {
			return null;
		}

		const dayAnchor = new Date(openRecording.start);
		const dayEntries = await this.fetchDayEntries(targetUserId, dayAnchor);

		const now = new Date();
		const nowRounded = roundToNearestMinutes(now, { nearestTo: 1 });
		const recStart = new Date(openRecording.start);
		const otherDayEntries = dayEntries.filter((e) => e.id !== openRecording.id && e.start && e.end);

		const capRaw = this.comegoSuggestion.getMaxCompliantWorkEndForOpenSegment(
			dayAnchor,
			recStart,
			nowRounded,
			otherDayEntries,
			[],
			wh,
			{ includeDailyMax: false }
		);
		let endAt = roundToNearestMinutes(capRaw, { nearestTo: 1 });
		if (isBefore(endAt, recStart)) endAt = roundToNearestMinutes(recStart, { nearestTo: 1 });
		endAt = this.clampEndToScheduleTarget(endAt, recStart, dayAnchor, otherDayEntries, user, targetUserId);
		if (isBefore(endAt, recStart)) endAt = roundToNearestMinutes(recStart, { nearestTo: 1 });
		if (isSameMinute(endAt, recStart)) endAt = this.ceilToNextHour(endAt);
		if (endAt.getTime() >= nowRounded.getTime()) return null;

		const violations = evaluateWorkingHours(dayEntries, wh, dayAnchor, {} as EvaluateWorkingHoursOptions);
		const breakViolations = violations.filter((v): v is WorkingHourViolation => v.type === "break");
		if (!breakViolations.length) return null;

		const primaryError = breakViolations.find((v) => v.entryID === openRecording.id) ?? breakViolations[0];

		const workspaceUser = user.workspace.users?.find((x) => x.id === targetUserId);

		const stopResult = await this.buildStoppedRecordingDraft(openRecording, recStart, endAt, workspaceUser, {
			user,
			targetUserId,
			dayEntries,
		});
		if (!stopResult) return null;

		const stoppedRecording = stopResult.stoppedRecording;
		const dialogEntries: ComegoTime[] = [stoppedRecording];
		if (stopResult.continuationRecording) dialogEntries.push(stopResult.continuationRecording);
		const pushDialogEntry = (entry?: ComegoTime) => {
			if (!entry) return;
			if (!isDraftId(entry.id) && entry.id && dialogEntries.some((e) => e.id === entry.id)) return;
			dialogEntries.push(entry);
		};

		const entriesAfterStop = dayEntries.map((e) => (e.id === openRecording.id ? stoppedRecording : e));
		const stopDayAnchor = startOfDay(endAt);
		const entriesForScheduleCheck = [
			...entriesAfterStop.filter((e) => e.start && e.end),
			...(stopResult.continuationRecording ? [stopResult.continuationRecording] : []),
		];
		const scheduleTargetReached = this.isScheduleTargetReachedForDay(
			user,
			targetUserId,
			stopDayAnchor,
			entriesForScheduleCheck
		);

		const isCurrentDayRecording = isSameDay(recStart, nowRounded);

		if (!isCurrentDayRecording && !scheduleTargetReached) {
			const requiredBreakMin = this.comegoSuggestion.getRequiredBreakDuration(
				dayAnchor,
				entriesAfterStop.filter((e) => e.start && e.end),
				[],
				wh
			);
			const requiredBreakMs = (requiredBreakMin ?? 0) * 60 * 1000;
			const isLongRecording = nowRounded.getTime() - recStart.getTime() >= LONG_RECORDING_THRESHOLD_MS;

			let pauseEnd: Date;
			let pauseRemainsActive: boolean;
			if (isLongRecording && requiredBreakMs > 0) {
				pauseEnd = roundToNearestMinutes(addMilliseconds(endAt, requiredBreakMs), { nearestTo: 1 });
				pauseRemainsActive = nowRounded.getTime() < pauseEnd.getTime();
			} else {
				pauseEnd = this.clampAutoPauseEnd(endAt, nowRounded);
				const pauseDurationMs = Math.max(0, pauseEnd.getTime() - endAt.getTime());
				pauseRemainsActive = pauseDurationMs < requiredBreakMs;
			}
			const pauseEffectiveEnd = pauseRemainsActive ? nowRounded : pauseEnd;
			const pauseSegments = this.buildPauseSegments(endAt, pauseEffectiveEnd, pauseRemainsActive);

			const pauseDrafts: ComegoTime[] = [];
			for (const segment of pauseSegments) {
				const draft: ComegoTime = {
					id: makeDraftId(),
					name: null,
					user: workspaceUser,
					start: segment.start.toISOString(),
					end: segment.end?.toISOString() ?? null,
					type: "pause",
				} as unknown as ComegoTime;
				pushDialogEntry(draft);
				pauseDrafts.push(draft);
			}

			if (!pauseRemainsActive && pauseSegments.length) {
				const lastPauseEnd = pauseSegments[pauseSegments.length - 1].end ?? pauseEnd;
				if (isLongRecording) {
					const fillWork = await this.buildFillWorkAfterPauseDraft(
						lastPauseEnd,
						workspaceUser,
						user,
						targetUserId,
						dayAnchor,
						pauseDrafts,
						entriesAfterStop
					);
					if (fillWork) pushDialogEntry(fillWork);
				} else {
					const resumeDraft: ComegoTime = {
						id: makeDraftId(),
						name: null,
						user: workspaceUser,
						start: lastPauseEnd.toISOString(),
						end: null,
						type: "work",
					} as unknown as ComegoTime;
					pushDialogEntry(resumeDraft);
				}
			}
		}

		const startDayAnchor = startOfDay(dayAnchor);
		const startDayEntries = this.filterEntriesForDay(dialogEntries, startDayAnchor);
		const laterDayEntries = dialogEntries.filter((e) => e.start && !isSameDay(new Date(e.start), startDayAnchor));

		let laterDayDialogData: WorkingHoursUpdateData | undefined;
		if (laterDayEntries.length > 0) {
			const laterAnchor = startOfDay(new Date(laterDayEntries[0].start!));
			laterDayDialogData = {
				time: {
					value: this.filterEntriesForDay(laterDayEntries, laterAnchor),
					date: laterAnchor,
					refetchFromDate: true,
				},
				forceRecreateDay: true,
				pendingRecordingFix: true,
				openRecording,
				disabledFields: isSubordinate ? ["user"] : undefined,
			};
		}

		return {
			openRecording,
			breakSessionKey,
			preventDeviatingWorkingHours: !!wh.preventDeviatingWorkingHours,
			dialogData: {
				time: {
					value: startDayEntries,
					date: startDayAnchor,
					refetchFromDate: true,
				},
				forceRecreateDay: true,
				pendingRecordingFix: true,
				openRecording,
				disabledFields: isSubordinate ? ["user"] : undefined,
				error: {
					message: primaryError.message,
					args: primaryError.args,
				},
			},
			laterDayDialogData,
		};
	}

	private filterEntriesForDay(entries: ComegoTime[], dayAnchor: Date): ComegoTime[] {
		return entries.filter((e) => e.start && isSameDay(new Date(e.start), dayAnchor));
	}

	private normalizeOpenRecording(
		entry: ComegoTime & {
			recording?: boolean;
			userId?: string;
			type?: ComegoTime["type"] | { name?: string };
		}
	): ComegoTime | null {
		const type = typeof entry.type === "string" ? entry.type : (entry.type as { name?: string } | null)?.name;
		const userId = entry.user?.id ?? entry.userId;
		const isRecording = entry.recording || !entry.end;
		if (!entry.start || !userId || type !== "work" || !isRecording) return null;
		return {
			...entry,
			user: entry.user?.id ? entry.user : ({ id: userId } as ComegoTime["user"]),
			end: null,
			type,
		} as ComegoTime;
	}

	private async fetchDayEntries(targetUserId: string, dayAnchor: Date): Promise<ComegoTime[]> {
		const start = startOfDay(dayAnchor);
		const end = endOfDay(start);
		try {
			const entries = await this.comegoService.adapter
				.post<ComegoTime[]>("/get/comego", {
					$filter: `start le '${end.toISOString()}' and start ge '${start.toISOString()}' and user__id eq '${targetUserId}'`,
				})
				.toPromise();
			if (entries?.length) (this.comegoQuery.__store__ as ComegoStore).upsertMany(entries);
			return (entries ?? []).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
		} catch (err) {
			log.error(err);
			return this.comegoQuery
				.getAll({
					filterBy: (x) => x.user?.id === targetUserId && isSameDay(new Date(x.start), dayAnchor),
				})
				.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
		}
	}

	private getDayCutEnd(anchor: Date): Date {
		return roundToNearestMinutes(
			set(startOfDay(anchor), { hours: DAY_CUT_HOUR, minutes: DAY_CUT_MINUTE, seconds: 0, milliseconds: 0 }),
			{ nearestTo: 1 }
		);
	}

	private getNextDayStart(anchor: Date): Date {
		return roundToNearestMinutes(
			set(startOfDay(addDays(anchor, 1)), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }),
			{
				nearestTo: 1,
			}
		);
	}

	/**
	 * Builds in-memory draft entries representing the stopped (and optionally split cross-day) recording.
	 * No API calls are made.
	 */
	private async buildStoppedRecordingDraft(
		openRecording: ComegoTime,
		recStart: Date,
		endAt: Date,
		workspaceUser: ComegoTime["user"] | undefined,
		scheduleContext?: {
			user: UserSettings;
			targetUserId: string;
			dayEntries: ComegoTime[];
		}
	): Promise<{ stoppedRecording: ComegoTime; continuationRecording?: ComegoTime } | null> {
		const effectiveEnd = endAt.getTime() < recStart.getTime() ? recStart : endAt;

		if (isSameDay(recStart, effectiveEnd)) {
			const stoppedRecording: ComegoTime = {
				...openRecording,
				end: effectiveEnd.toISOString(),
			};
			return { stoppedRecording };
		}

		const dayEndCut = this.getDayCutEnd(recStart);
		const nextDayStart = this.getNextDayStart(recStart);
		const otherOnStartDay =
			scheduleContext?.dayEntries.filter(
				(e) => e.id !== openRecording.id && e.start && e.end && isSameDay(new Date(e.start), recStart)
			) ?? [];
		let firstEnd = dayEndCut.getTime() > recStart.getTime() ? dayEndCut : recStart;
		if (scheduleContext) {
			firstEnd = this.clampEndToScheduleTarget(
				firstEnd,
				recStart,
				recStart,
				otherOnStartDay,
				scheduleContext.user,
				scheduleContext.targetUserId
			);
		}

		const stoppedRecording: ComegoTime = {
			...openRecording,
			end: firstEnd.toISOString(),
		};

		let continuationRecording: ComegoTime | undefined;
		if (effectiveEnd.getTime() > nextDayStart.getTime()) {
			let continuationEnd = effectiveEnd;
			if (scheduleContext) {
				const nextDayEntries = await this.fetchDayEntries(scheduleContext.targetUserId, nextDayStart);
				continuationEnd = this.clampEndToScheduleTarget(
					effectiveEnd,
					nextDayStart,
					nextDayStart,
					nextDayEntries.filter((e) => e.start && e.end),
					scheduleContext.user,
					scheduleContext.targetUserId
				);
			}
			if (continuationEnd.getTime() > nextDayStart.getTime()) {
				continuationRecording = {
					id: makeDraftId(),
					name: openRecording.name ?? null,
					user: workspaceUser,
					start: nextDayStart.toISOString(),
					end: continuationEnd.toISOString(),
					type: "work",
				} as unknown as ComegoTime;
			}
		}

		return { stoppedRecording, continuationRecording };
	}

	/**
	 * Builds an optional in-memory draft work entry to fill the remaining schedule target
	 * after a long-recording pause. No API calls are made.
	 */
	private async buildFillWorkAfterPauseDraft(
		lastPauseEnd: Date,
		workspaceUser: ComegoTime["user"] | undefined,
		user: UserSettings,
		targetUserId: string,
		dayAnchor: Date,
		pauseDrafts: ComegoTime[],
		entriesAfterStop: ComegoTime[]
	): Promise<ComegoTime | undefined> {
		const workDayAnchor = startOfDay(lastPauseEnd);
		const dayEntries = isSameDay(workDayAnchor, startOfDay(dayAnchor))
			? entriesAfterStop
			: await this.fetchDayEntries(targetUserId, workDayAnchor);

		const schedule = getActiveSchedule({ ...user, id: targetUserId }, workDayAnchor);
		const targetMs =
			this.comegoSuggestion.getTargetHoursForDay(schedule, workDayAnchor) ?? DEFAULT_SCHEDULE_TARGET_MS;
		const entriesForRemaining = [
			...dayEntries.filter((e) => e.start && e.end && isSameDay(new Date(e.start), workDayAnchor)),
			...pauseDrafts.filter((e) => isSameDay(new Date(e.start), workDayAnchor)),
		];
		const totalWorkMs = this.sumWorkMsForDay(entriesForRemaining, workDayAnchor);
		const remainingMs = targetMs - totalWorkMs;
		if (remainingMs <= 0) return undefined;

		const dayEnd = endOfDay(workDayAnchor);
		const workEnd = roundToNearestMinutes(minDate([addMilliseconds(lastPauseEnd, remainingMs), dayEnd]), {
			nearestTo: 1,
		});
		if (workEnd.getTime() <= lastPauseEnd.getTime()) return undefined;

		return {
			id: makeDraftId(),
			name: null,
			user: workspaceUser,
			start: lastPauseEnd.toISOString(),
			end: workEnd.toISOString(),
			type: "work",
		} as unknown as ComegoTime;
	}

	private getScheduleTargetMs(user: UserSettings, targetUserId: string, date: Date): number {
		const schedule = getActiveSchedule({ ...user, id: targetUserId }, date);
		return this.comegoSuggestion.getTargetHoursForDay(schedule, date) ?? DEFAULT_SCHEDULE_TARGET_MS;
	}

	private sumWorkMsForDay(entries: ComegoTime[], dayAnchor: Date): number {
		return entries
			.filter((e) => e.type === "work" && e.start && e.end && isSameDay(new Date(e.start), dayAnchor))
			.reduce((sum, e) => sum + Math.max(0, new Date(e.end).getTime() - new Date(e.start).getTime()), 0);
	}

	private isScheduleTargetReachedForDay(
		user: UserSettings,
		targetUserId: string,
		dayAnchor: Date,
		entries: ComegoTime[]
	): boolean {
		const targetMs = this.getScheduleTargetMs(user, targetUserId, dayAnchor);
		const totalWorkMs = this.sumWorkMsForDay(entries, dayAnchor);
		return totalWorkMs >= targetMs;
	}

	private clampEndToScheduleTarget(
		endAt: Date,
		segmentStart: Date,
		dayAnchor: Date,
		otherDayEntries: ComegoTime[],
		user: UserSettings,
		targetUserId: string
	): Date {
		const scheduleTargetMs = this.getScheduleTargetMs(user, targetUserId, dayAnchor);
		const otherWorkMs = this.sumWorkMsForDay(otherDayEntries, dayAnchor);
		const maxSegmentMs = Math.max(0, scheduleTargetMs - otherWorkMs);
		const maxEnd = roundToNearestMinutes(addMilliseconds(segmentStart, maxSegmentMs), { nearestTo: 1 });
		return endAt.getTime() > maxEnd.getTime() ? maxEnd : endAt;
	}

	private clampAutoPauseEnd(pauseStart: Date, candidateEnd: Date): Date {
		const maxEnd = roundToNearestMinutes(addHours(pauseStart, 10), { nearestTo: 1 });
		if (candidateEnd.getTime() <= maxEnd.getTime()) return candidateEnd;
		return maxEnd.getTime() < pauseStart.getTime() ? roundToNearestMinutes(pauseStart, { nearestTo: 1 }) : maxEnd;
	}

	private buildPauseSegments(
		pauseStart: Date,
		pauseEnd: Date,
		pauseRemainsActive: boolean
	): Array<{ start: Date; end: Date | null }> {
		if (pauseRemainsActive) {
			return [{ start: roundToNearestMinutes(pauseStart, { nearestTo: 1 }), end: null }];
		}

		const segments: Array<{ start: Date; end: Date | null }> = [];
		let cursor = roundToNearestMinutes(pauseStart, { nearestTo: 1 });
		const targetEnd = roundToNearestMinutes(pauseEnd, { nearestTo: 1 });

		if (cursor.getTime() >= targetEnd.getTime()) return segments;

		for (let i = 0; i < 366; i++) {
			if (isSameDay(cursor, targetEnd)) {
				if (cursor.getTime() < targetEnd.getTime()) {
					segments.push({ start: cursor, end: targetEnd });
				}
				break;
			}

			const dayCut = this.getDayCutEnd(cursor);
			if (cursor.getTime() < dayCut.getTime()) {
				segments.push({ start: cursor, end: dayCut });
			}
			cursor = this.getNextDayStart(cursor);
		}

		return segments;
	}

	private ceilToNextHour(date: Date) {
		return addHours(
			set(date, {
				minutes: 0,
				seconds: 0,
				milliseconds: 0,
			}),
			1
		);
	}
}
